import type { Bulle, DateAuPlusTot, Maillon } from '../partage/api/types';
import { delaiEnMots, enFrancais, instantEnToutesLettres, selonLaDateAuPlusTot } from '../partage/datesEnMots';
import {
  AU_PLUS_TOT,
  CHARGES,
  delaiDepuis,
  depuisLe,
  dureeDuRendezVous,
  ECHEANCE,
  LE_MONDE_MET,
  RENDEZ_VOUS,
  leMondeMet,
  majuscule,
  MOTS_ETAT,
  MOTS_PRIORITE,
  NON_REGLEE,
  PAS_DE_DATE_DU_PLAN,
  PAS_ENCORE_CALCULABLE,
  proprietesEnMots,
  REMONTE_DANS_MAINTENANT,
} from '../partage/vocabulaire';

/**
 * Ce que la fiche dit d'une bulle, sans rendu (chantier `revue-design`, ticket 02,
 * `design/fiche.html` et `design/fiche-vide.html`). Rien ne s'y calcule sur le moteur : la
 * place dans « Maintenant », l'importance et ce qui la retient viennent du serveur ; on choisit
 * et on met en phrase.
 */

/**
 * Le bandeau « Remonte dans Maintenant : … » : seulement quand la bulle flotte (Prioritaire ou
 * « Pendant ce temps », décision d'Igor du 2026-10-03, « La fiche » 1), avec le pourquoi que sa
 * ligne écrit là-bas. Sous l'eau, pas candidate, ou ouverte depuis une réponse qui ne porte pas
 * sa place : rien.
 */
export function remonteDansMaintenant(bulle: Pick<Bulle, 'maintenant'>): string | null {
  const m = bulle.maintenant;
  if (!m || m.place === 'sous_l_eau' || !m.pourquoi) return null;
  return `${REMONTE_DANS_MAINTENANT} : ${majuscule(m.pourquoi)}`;
}

/**
 * « 3 sur 6 ‹ › » : la place de la bulle parmi ses sœurs, déjà rangées, et les voisines où
 * mènent les flèches. Rien quand elle est seule : il n'y a nulle part où aller. Aux deux bouts,
 * la flèche n'a pas de voisine (pas de tour : on sait où l'on est).
 */
export function placeParmiLesSoeurs(
  soeurs: { id: string }[],
  id: string
): { position: number; total: number; precedente: string | null; suivante: string | null } | null {
  const i = soeurs.findIndex((s) => s.id === id);
  if (i < 0 || soeurs.length < 2) return null;
  return {
    position: i + 1,
    total: soeurs.length,
    precedente: soeurs[i - 1]?.id ?? null,
    suivante: soeurs[i + 1]?.id ?? null,
  };
}

/** La mère : l'avant-dernier maillon du chemin. Un projet n'en a pas. */
export function laMere(chemin: Maillon[]): Maillon | null {
  return chemin.length > 1 ? (chemin[chemin.length - 2] ?? null) : null;
}

/**
 * Les trois pastilles des réglages : le mot de chacune, ou `null` quand rien n'est réglé (la
 * pastille dit alors « + Importance », « + Durée », « + Où », en pointillé, comme la maquette
 * de la bulle vide).
 *
 * L'importance de la pastille est **la sienne**, celle qu'on règle en la cliquant, comme la
 * maquette de la bulle remplie (« Importance non réglée » quand une mère la rend Urgent). Celle
 * qu'elle reçoit d'une mère se dit au survol (`importanceVient`, « « Les papiers » est
 * Urgent »), et dans le bandeau quand c'est elle qui la fait remonter. Rien ni chez elle ni
 * au-dessus : `null`, « + Importance ».
 */
export function motsDesReglages(
  bulle: Pick<Bulle, 'priorite' | 'prioriteChoisie' | 'effortMin' | 'canal' | 'heuresOuvrables'>
): { importance: string | null; importanceVient: string | null; duree: string | null; ou: string | null } {
  const tri = motsDuTri(bulle);
  // La seule différence avec le téléphone : une importance reçue d'une mère se dit « non réglée ».
  const importance = tri.importance ?? (bulle.priorite ? NON_REGLEE.toLocaleLowerCase('fr-FR') : null);
  const ou = proprietesEnMots(bulle, ['lieu', 'heures']).join(' · ');
  return {
    importance,
    importanceVient: bulle.priorite ? majuscule(bulle.priorite.raison) : null,
    duree: tri.duree,
    ou: ou || null,
  };
}

/**
 * Les mots des réglages de tri, les mêmes aux deux fiches (chantier `nettoyage-fiche`, ticket
 * 01) : la durée, le lieu de travail et l'importance **choisie à la main**, ou `null` quand
 * rien n'est réglé. Une valeur que l'app ne connaît pas se dit telle quelle. Chaque fiche y
 * ajoute ce qui lui est propre : l'ordinateur, les heures d'ouverture avec le lieu et
 * « non réglée » pour une importance reçue (`motsDesReglages`) ; le téléphone, ses pastilles
 * (`groupesDesReglages`).
 */
export function motsDuTri(bulle: Pick<Bulle, 'prioriteChoisie' | 'effortMin' | 'canal'>): {
  duree: string | null;
  lieu: string | null;
  importance: string | null;
} {
  const [duree = null] = proprietesEnMots(bulle, ['duree']);
  const [lieu = null] = proprietesEnMots(bulle, ['lieu']);
  return {
    duree,
    lieu,
    importance: bulle.prioriteChoisie ? MOTS_PRIORITE[bulle.prioriteChoisie] : null,
  };
}

/** Une pastille de la face de la fiche Classique : ce qui est réglé, et l'intercalaire qui le règle. */
export interface PastilleDeLaFace {
  cle: 'duree' | 'ou' | 'difficulte' | 'importance';
  mot: string;
}

/**
 * Les pastilles de la face de la fiche Classique (chantier reglages-de-la-fiche, modification 03 ;
 * maquette `.pastilles`) : seulement ce qui est réglé, dans l'ordre de la maquette, la durée, où,
 * la difficulté en étoiles, l'importance. Les mots sont ceux des Réglages (`motsDesReglages`,
 * `motsDuTri`) ; l'importance reçue d'une mère n'est pas réglée chez elle, elle n'a pas de pastille.
 */
export function pastillesDeLaFace(
  bulle: Pick<Bulle, 'priorite' | 'prioriteChoisie' | 'effortMin' | 'canal' | 'heuresOuvrables' | 'charge'>
): PastilleDeLaFace[] {
  const mots = motsDesReglages(bulle);
  const importance = motsDuTri(bulle).importance;
  const pastilles: PastilleDeLaFace[] = [];
  if (mots.duree) pastilles.push({ cle: 'duree', mot: mots.duree });
  if (mots.ou) pastilles.push({ cle: 'ou', mot: mots.ou });
  if (bulle.charge != null) pastilles.push({ cle: 'difficulte', mot: etoilesEnTexte(bulle.charge) });
  if (importance) pastilles.push({ cle: 'importance', mot: importance });
  return pastilles;
}

/** La difficulté en étoiles, pleines puis vides, sur les crans de la fiche : « ★★☆☆☆ ». */
function etoilesEnTexte(charge: number): string {
  return CHARGES.map((cran) => (cran <= charge ? '★' : '☆')).join('');
}

/** Une ligne de « Quand », sur la face : ce qui est posé du temps, et l'intercalaire Dates. */
export interface LigneDuQuand {
  cle: 'echeance' | 'rdv' | 'delai' | 'plusTot';
  nom: string;
  valeur: string;
}

/**
 * « Quand », sur la face de la fiche Classique (chantier reglages-de-la-fiche, modification 03 ;
 * maquette `.rubrique`, `.champ`) : l'échéance, le rendez-vous, le délai du monde, s'ils sont posés,
 * avec les mots que leurs lignes disent dans les Réglages (`ChampsQuand`) ; puis la date au plus
 * tôt, telle que le serveur la donne, quand c'est une date.
 */
export function quandEnMots(
  bulle: Pick<Bulle, 'dateEcheance' | 'debut' | 'dureeMin' | 'delaiJours' | 'delaiMois' | 'dateAction' | 'dateAuPlusTot'>
): LigneDuQuand[] {
  const lignes: LigneDuQuand[] = [];
  const temps = valeursDuQuand(bulle);
  if (temps.echeance) lignes.push({ cle: 'echeance', nom: ECHEANCE, valeur: temps.echeance });
  if (temps.rdv) lignes.push({ cle: 'rdv', nom: RENDEZ_VOUS, valeur: temps.rdv });
  if (temps.delai) lignes.push({ cle: 'delai', nom: LE_MONDE_MET, valeur: temps.delai });
  if (bulle.dateAuPlusTot.sorte === 'date') {
    lignes.push({ cle: 'plusTot', nom: AU_PLUS_TOT, valeur: dateDuPlan(bulle.dateAuPlusTot) });
  }
  return lignes;
}

/**
 * Ce que disent les trois temps d'une bulle, posés : l'échéance, le rendez-vous (et sa durée), le
 * délai du monde (et le jour où tu as agi) ; `null` pour ce qui n'est pas posé. Écrit une fois,
 * lu par la face (`quandEnMots`) et par les lignes des Réglages (`ChampsQuand`).
 */
export function valeursDuQuand(
  bulle: Pick<Bulle, 'dateEcheance' | 'debut' | 'dureeMin' | 'delaiJours' | 'delaiMois' | 'dateAction'>
): { echeance: string | null; rdv: string | null; delai: string | null } {
  const delai = delaiEnMots(bulle);
  return {
    echeance: bulle.dateEcheance ? enFrancais(bulle.dateEcheance, true) : null,
    rdv: bulle.debut ? instantEnToutesLettres(bulle.debut) + (bulle.dureeMin ? dureeDuRendezVous(bulle.dureeMin) : '') : null,
    delai: delai ? delaiDepuis(delai, bulle.dateAction) : null,
  };
}

/** Une pièce de « Joint » : ce que la bulle porte déjà (son lien, son lieu, son contact). */
export interface PieceJointe {
  sorte: 'lien' | 'adresse' | 'contact';
  nom: string;
}

/**
 * Ce qui est joint à la bulle, dans l'ordre de la maquette (lien web, adresse, contact). Les
 * documents sont de la V2 : aucun n'existe encore. Un lien qui n'est ni `http:` ni `https:` ne
 * s'y montre pas (il ne s'ouvrirait pas, et un `javascript:` cliquable est une faille).
 */
export function piecesJointes(
  bulle: Pick<Bulle, 'lien' | 'lieuTexte' | 'contactNom' | 'contactTel'>
): PieceJointe[] {
  const pieces: PieceJointe[] = [];
  if (bulle.lien && /^https?:\/\//i.test(bulle.lien)) {
    pieces.push({ sorte: 'lien', nom: bulle.lien.replace(/^https?:\/\//i, '').replace(/\/$/, '') });
  }
  if (bulle.lieuTexte) pieces.push({ sorte: 'adresse', nom: bulle.lieuTexte });
  const contact = [bulle.contactNom, bulle.contactTel].filter(Boolean).join(' · ');
  if (contact) pieces.push({ sorte: 'contact', nom: contact });
  return pieces;
}

/**
 * L'initiale d'un contact, dans le rond de la fiche Contact (maquette `.avatar-c`) : la première
 * lettre ou le premier chiffre du nom, en majuscule (« M. Martin » donne « M »). Rien à montrer :
 * une chaîne vide.
 */
export function initialeDuContact(nom: string): string {
  const lettre = nom.match(/[\p{L}\p{N}]/u)?.[0] ?? '';
  return lettre.toLocaleUpperCase('fr');
}

/**
 * L'itinéraire vers une adresse : une recherche Google Maps, ouverte dans un nouvel onglet
 * (décision d'Igor du 2026-10-20). Une adresse construite, qui commence toujours par `https:`.
 */
export function itineraireVers(adresse: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(adresse.trim())}`;
}

/**
 * Le numéro d'un contact, appelé ou écrit d'un toucher au téléphone (chantier telephone-habit,
 * 04, maquette `.carte-visite` : Appeler, SMS) : les chiffres et le « + » seulement.
 */
export function appelerLe(tel: string): string {
  return `tel:${tel.replace(/[^\d+]/g, '')}`;
}
export function smsA(tel: string): string {
  return `sms:${tel.replace(/[^\d+]/g, '')}`;
}

/** L'e-mail d'un contact, ouvert dans la messagerie. */
export function messageA(email: string): string {
  return `mailto:${encodeURIComponent(email.trim()).replace(/%40/g, '@')}`;
}

/**
 * Le bandeau du monde de la fiche Plan (chantier types-de-fiche, modification 05 ; maquette
 * `.bandeau-monde`) : seulement en instruction, « En instruction · le monde met 15 jours », puis,
 * mis en avant, « depuis le 8 octobre ». Les mots que la fiche dit déjà (« Le monde met »), rien
 * de compté ici. `null` hors instruction.
 */
export function bandeauDuMonde(
  bulle: Pick<Bulle, 'etat' | 'delaiJours' | 'delaiMois' | 'dateAction'>
): { debut: string; depuis: string | null } | null {
  if (bulle.etat !== 'en_attente_confirmation') return null;
  const delai = delaiEnMots(bulle);
  return {
    // « Le monde met 15 jours », « Depuis le 8 octobre » : les phrases de la fiche, en milieu de ligne.
    debut: [MOTS_ETAT.en_attente_confirmation, delai ? enMinuscule(leMondeMet(delai)) : null]
      .filter(Boolean)
      .join(' · '),
    depuis: bulle.dateAction ? enMinuscule(depuisLe(bulle.dateAction)) : null,
  };
}

/** La date au plus tôt de la fiche Plan, telle que le serveur la donne : « 7 novembre ». */
export function dateDuPlan(date: DateAuPlusTot): string {
  return selonLaDateAuPlusTot(date, {
    date: (jour) => enFrancais(jour),
    quandTuVeux: PAS_DE_DATE_DU_PLAN,
    pasCalculable: PAS_ENCORE_CALCULABLE,
  });
}

function enMinuscule(phrase: string): string {
  return phrase.charAt(0).toLocaleLowerCase('fr-FR') + phrase.slice(1);
}
